import { IAuthenticationPort } from '../ports/AuthenticationPort';
import { AzureAuthConfig } from '../adapters/azure/AzureAuthAdapter';
import { GcpAuthConfig } from '../adapters/gcp/GcpAuthAdapter';
import { ForgeGcpAuthConfig, ForgeGcpAuthAdapter } from '../adapters/gcp/ForgeGcpAuthAdapter';
import { getAuthConfig, CloudProvider, ProviderType } from '../config/authConfig';

function resolveAzureConfig(
  env: any,
  providerType: ProviderType
): AzureAuthConfig {
  const origin = window.location.origin;
  
  if (providerType === 'mcd') {
    return {
      authority: env.AUTHORITY,
      clientId: env.AUTHORITY_CLIENT_ID_MCD,
      tenantId: env.TENANT_ID,
      redirectUri: `${origin}/signin-callback.html`,
      postLogoutRedirectUri: `${origin}/app/login-redirect`,
      silentRedirectUri: `${origin}/silent-renew.html`,
      scopes: env.API_SCOPES || ['openid', 'offline', 'profile', 'consup.cloud.api.gateway', 'email']
    };
  }
  
  if (providerType === 'honsso') {
    return {
      authority: env.AUTHORITY_HON_SSO,
      clientId: env.AUTHORITY_CLIENT_ID_HON_SSO,
      tenantId: env.TENANT_ID,
      redirectUri: `${origin}/signin-callback.html`,
      postLogoutRedirectUri: `${origin}/app/login-redirect`,
      silentRedirectUri: `${origin}/silent-renew.html`,
      scopes: env.API_SCOPES_HON_SSO || ['profile', 'openid', 'email', 'https://consup.cloud.api.gateway.com'],
      metadata: {
        authorization_endpoint: env.HON_SSO_AUTHORIZATION_ENDPOINT,
        token_endpoint: env.HON_SSO_TOKEN_ENDPOINT,
        end_session_endpoint: env.HON_SSO_END_SESSION_ENDPOINT,
        userinfo_endpoint: env.HON_SSO_USERINFO_ENDPOINT
      }
    };
  }
  
  return {
    authority: env.AUTHORITY,
    clientId: env.AUTHORITY_CLIENT_ID,
    tenantId: env.TENANT_ID,
    redirectUri: `${origin}/signin-callback.html`,
    postLogoutRedirectUri: `${origin}/app/login-redirect`,
    silentRedirectUri: `${origin}/silent-renew.html`,
    scopes: env.API_SCOPES || ['openid', 'offline', 'profile', 'consup.cloud.api.gateway', 'email']
  };
}

function resolveForgeGcpConfig(env: any): ForgeGcpAuthConfig {
  const origin = window.location.origin;
  return {
    authority: env.FORGE_IAM_AUTHORITY_GCP,
    clientId: env.FORGE_IAM_CLIENT_ID_GCP,
    redirectUri: `${origin}/signin-callback.html`,
    postLogoutRedirectUri: `${origin}/app/login-redirect`,
    silentRedirectUri: `${origin}/silent-renew.html`,
    scopes: env.GCP_SCOPES || ['openid', 'profile', 'email']
  };
}

function validateForgeGcpConfig(config: ForgeGcpAuthConfig): boolean {
  return !!(config.authority && config.clientId);
}

function resolveGcpConfig(env: any): GcpAuthConfig {
  const origin = window.location.origin;
  
  return {
    clientId: env.GCP_CLIENT_ID,
    redirectUri: `${origin}/signin-callback.html`,
    postLogoutRedirectUri: `${origin}/app/login-redirect`,
    silentRedirectUri: `${origin}/silent-renew.html`,
    scopes: env.GCP_SCOPES || ['openid', 'profile', 'email'],
    hostedDomain: env.GCP_HOSTED_DOMAIN
  };
}

function validateAzureConfig(config: AzureAuthConfig): boolean {
  return !!(config.authority && config.clientId && config.tenantId);
}

function validateGcpConfig(config: GcpAuthConfig): boolean {
  return !!config.clientId;
}

export async function createAuthAdapter(overrides?: {
  cloudProvider?: CloudProvider;
  providerType?: ProviderType;
}): Promise<IAuthenticationPort> {
  const env = window.ENV;
  const authConfig = getAuthConfig();
  
  const cloud = overrides?.cloudProvider ?? authConfig.cloudProvider;
  const providerType = overrides?.providerType ?? authConfig.providerType;
  
  if (cloud === 'gcp') {
    if (env.FORGE_IAM_CLIENT_ID_GCP) {
      const config = resolveForgeGcpConfig(env);
      if (!validateForgeGcpConfig(config)) {
        throw new Error('Invalid configuration for Forge GCP adapter: FORGE_IAM_AUTHORITY_GCP and FORGE_IAM_CLIENT_ID_GCP are required');
      }
      return new ForgeGcpAuthAdapter(config);
    }
    const { GcpAuthAdapter } = await import('../adapters/gcp/GcpAuthAdapter');
    const config = resolveGcpConfig(env);
    if (!validateGcpConfig(config)) {
      throw new Error('Invalid configuration for gcp adapter: GCP_CLIENT_ID is required');
    }
    return new GcpAuthAdapter(config);
  }
  
  if (cloud === 'azure') {
    const { AzureAuthAdapter } = await import('../adapters/azure/AzureAuthAdapter');
    const config = resolveAzureConfig(env, providerType);
    if (!validateAzureConfig(config)) {
      throw new Error('Invalid configuration for azure adapter: authority, clientId, and tenantId are required');
    }
    return new AzureAuthAdapter(config);
  }
  
  throw new Error(`No identity provider configuration found for ${cloud}/${providerType}`);
}
